/**
 * Utility functions for NorthBuilt Compass
 */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import CONFIG from '../config';

/**
 * Merge Tailwind CSS classes with proper conflict resolution
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Generate a UUID v4
 * Uses crypto.randomUUID() if available, otherwise falls back to custom implementation
 */
export function generateUUID(): string {
  // Modern browsers support crypto.randomUUID()
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback implementation for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Preprocess markdown text to restore logical line breaks.
 *
 * Bedrock Knowledge Base often strips newlines from document content during indexing.
 * This function detects common markdown patterns and inserts line breaks where they
 * should logically exist to enable proper rendering.
 *
 * Patterns detected:
 * - Markdown headers (#, ##, ###, etc.)
 * - Bold labels followed by content (**Label:** value)
 * - List items (-, *, numbered lists)
 * - Paragraph breaks (double spaces or sentence ends before headers)
 *
 * @param text - Raw markdown text that may be missing line breaks
 * @returns Text with logical line breaks restored
 */
export function preprocessMarkdownSnippet(text: string): string {
  if (!text) return '';

  let result = text;

  // Add line breaks before markdown headers (# ## ### etc.)
  // Matches headers not already at the start of a line or after newline
  // Uses space before # to avoid matching # inside words
  result = result.replace(/(\S)\s+(#{1,6}\s)/g, '$1\n\n$2');

  // Add line breaks before bold labels that look like metadata
  // Pattern: **Label:** followed by content
  // Must have non-newline character before it
  result = result.replace(/(\S)\s+(\*\*[A-Z][^*]+:\*\*)/g, '$1\n$2');

  // Add line breaks before list items (- or * at start of item)
  // Pattern: text followed by space + list marker + space + letter
  result = result.replace(/(\S)\s+([-*])\s+([A-Za-z])/g, '$1\n$2 $3');

  // Add line breaks before numbered list items
  result = result.replace(/(\S)\s+(\d+\.)\s+([A-Za-z])/g, '$1\n$2 $3');

  // Add line breaks after sentences that are followed by headers or bold labels
  // This helps separate paragraphs
  result = result.replace(/([.!?])\s+(#{1,6}\s)/g, '$1\n\n$2');
  result = result.replace(/([.!?])\s+(\*\*[A-Z])/g, '$1\n\n$2');

  // Clean up any excessive line breaks (more than 2 consecutive)
  result = result.replace(/\n{3,}/g, '\n\n');

  return result.trim();
}

/**
 * Validate that a string is a valid URL.
 *
 * Uses the URL constructor for validation, which handles edge cases
 * like malformed URLs, invalid protocols, etc.
 *
 * @param url - String to validate
 * @returns true if valid URL, false otherwise
 */
export function isValidUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;

  try {
    const parsed = new URL(url);
    // Only allow http and https protocols for security
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Generate a Gravatar URL from an email address
 * Uses SHA-256 hash (supported by Gravatar) since crypto.subtle is available
 */
export async function getGravatarUrl(
  email: string,
  size: number = 80,
): Promise<string> {
  try {
    // Normalize email: lowercase and trim
    const normalizedEmail = email.toLowerCase().trim();

    // Hash the email with SHA-256
    const encoder = new TextEncoder();
    const data = encoder.encode(normalizedEmail);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    // Return Gravatar URL with default fallback to identicon
    return `https://www.gravatar.com/avatar/${hashHex}?s=${size}&d=identicon`;
  } catch (error) {
    if (CONFIG.DEBUG) {
      console.error('Error generating Gravatar URL:', error);
    }
    // Fallback to a default avatar
    return `https://www.gravatar.com/avatar/00000000000000000000000000000000?s=${size}&d=mp`;
  }
}
